import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { copyTextToClipboard } from "../../lib/clipboard.js";
import { formatMessageLines, selfDisplayName } from "../../lib/message-export.js";
import { useRealtimeStore } from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";

// Экспорт чата текстом (раунд 46): «весь чат» или «N сообщений», затем
// «Скопировать» или «Сохранить .txt». Собирает страницы через
// store.chatExport (команда chat.exportHistory), формат строк — тот же, что
// у Ctrl+C: «[дата время] Имя: текст».

const ALL_CHAT_LIMIT = 1_000_000;

interface ExportChatModalProps {
  chatId: string;
  chatTitle: string;
  onClose(): void;
}

export const ExportChatModal = ({ chatId, chatTitle, onClose }: ExportChatModalProps) => {
  const startChatExport = useRealtimeStore((state) => state.startChatExport);
  const cancelChatExport = useRealtimeStore((state) => state.cancelChatExport);
  const chatExport = useRealtimeStore((state) => state.chatExport);
  const selfName = useRealtimeStore((state) => selfDisplayName(state.selfIdentity));
  const timeZone = useUiPrefs((state) => state.timeZone);
  const [mode, setMode] = useState<"all" | "count">("all");
  const [countInput, setCountInput] = useState("100");
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Закрытие модалки отменяет незавершённый сбор — иначе страницы летят в
  // стор впустую.
  useEffect(() => () => cancelChatExport(), [cancelChatExport]);

  const activeExport = chatExport && chatExport.chatId === chatId ? chatExport : null;
  const isPending = activeExport?.status === "pending";
  const isDone = activeExport?.status === "done";
  const parsedCount = Math.max(1, Math.min(100_000, Math.floor(Number(countInput) || 0)));
  const chatTitleForLines = chatTitle;

  const exportedText = useMemo(() => {
    if (!isDone || !activeExport) {
      return "";
    }
    // Для саппортов гейтвей срезает имена — подставляем (замаскированное)
    // название чата, как оно видно в интерфейсе.
    return formatMessageLines(
      activeExport.lines.map((line) => ({
        ...line,
        senderName: line.senderName ?? (line.outgoing ? undefined : chatTitleForLines)
      })),
      { selfName, timeZone }
    );
  }, [activeExport, chatTitleForLines, isDone, selfName, timeZone]);

  const start = (): void => {
    setNotice(null);
    startChatExport(chatId, mode === "all" ? ALL_CHAT_LIMIT : parsedCount);
  };

  const copy = async (): Promise<void> => {
    if (exportedText.length === 0) {
      setNotice("В выбранном диапазоне нет текста.");
      return;
    }
    const ok = await copyTextToClipboard(exportedText);
    setNotice(ok === false ? "Не удалось скопировать — буфер обмена недоступен." : "Скопировано в буфер обмена.");
  };

  const saveTxt = (): void => {
    if (exportedText.length === 0) {
      setNotice("В выбранном диапазоне нет текста.");
      return;
    }
    const blob = new Blob([exportedText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const safeTitle = chatTitle.replace(/[\\/:*?"<>|]+/g, "_").slice(0, 60) || "chat";
    anchor.href = url;
    anchor.download = `${safeTitle}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setNotice("Файл сохранён.");
  };

  return createPortal(
    <div className="folder-editor" role="dialog" aria-modal="true" aria-label="Экспорт чата">
      <button type="button" className="folder-editor__backdrop" onClick={onClose} aria-label="Закрыть" />
      <div className="folder-editor__panel export-chat">
        <div className="folder-editor__header">
          <strong>Экспорт — «{chatTitle}»</strong>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="export-chat__options">
          <label className="export-chat__option">
            <input
              type="radio"
              name="export-mode"
              checked={mode === "all"}
              onChange={() => setMode("all")}
              disabled={isPending}
            />
            <span>Весь чат (только текст)</span>
          </label>
          <label className="export-chat__option">
            <input
              type="radio"
              name="export-mode"
              checked={mode === "count"}
              onChange={() => setMode("count")}
              disabled={isPending}
            />
            <span>Последние</span>
            <input
              type="number"
              min={1}
              max={100000}
              value={countInput}
              onChange={(event) => setCountInput(event.target.value)}
              onFocus={() => setMode("count")}
              disabled={isPending}
              className="export-chat__count"
              aria-label="Сколько сообщений"
            />
            <span>сообщений</span>
          </label>
        </div>
        <div className="folder-autopick__note">
          {isPending
            ? `Собираем… получено ${activeExport?.lines.length ?? 0} сообщений`
            : isDone
              ? `Готово: ${activeExport?.lines.length ?? 0} сообщений${
                  mode === "all" ? " (весь чат)" : ""
                }.`
              : activeExport?.status === "error"
                ? activeExport.message ?? "Ошибка экспорта."
                : "Сообщения и наши, и собеседника; медиа — подписью в скобках."}
        </div>
        {notice ? <div className="folder-autopick__note">{notice}</div> : null}
        <div className="folder-editor__actions">
          <button type="button" className="button button--ghost" onClick={onClose}>
            Закрыть
          </button>
          {!isDone ? (
            <button type="button" className="button" onClick={start} disabled={isPending}>
              {isPending ? "Собираем…" : "Собрать"}
            </button>
          ) : (
            <>
              <button type="button" className="button button--ghost" onClick={start}>
                Собрать заново
              </button>
              <button type="button" className="button button--ghost" onClick={saveTxt}>
                Сохранить .txt
              </button>
              <button type="button" className="button" onClick={() => void copy()}>
                Скопировать
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
